import React, { useContext, useEffect, useRef } from 'react';
import style from './index.module.css';
import { AppContext } from '../../../app';
import { getDateRangeFromQueryString, setDateRangeInQueryString } from '../../../utils/urlUtils';
import { connectRange } from 'react-instantsearch-dom';
import Radio from '../../radio';

type RangeRefinement = {
    min?: number;
    max?: number;
};

type CustomRangeInputProps = {
    currentRefinement: RangeRefinement;
    refine(value?: RangeRefinement | false): void;
};

const DateList = ({ currentRefinement, refine }: CustomRangeInputProps) => {
    const appContext = useContext(AppContext);

    const radioRefs = {
        all: useRef<HTMLLIElement>(null),
        past: useRef<HTMLLIElement>(null),
        upcoming: useRef<HTMLLIElement>(null),
    };

    // Build refinements (if all items are in the past,
    // upcoming items won't exist, and vice versa)
    const refinementRefs = {
        all: useRef<RangeRefinement>({}),
        past: useRef<RangeRefinement>({}),
        upcoming: useRef<RangeRefinement>({}),
    };

    // Cache the min/max we see in the first (populated) Algolia refinement
    if (
        !refinementRefs.all.current.min &&
        !refinementRefs.all.current.max &&
        currentRefinement.min &&
        currentRefinement.max
    ) {
        const today = Date.now();
        refinementRefs.all.current.min = currentRefinement.min;
        refinementRefs.all.current.max = currentRefinement.max;

        // Build past and upcoming refinements with +/- 1ms to
        // ensure that they're different queries from the all query
        // (this matters when there are no past or no upcoming)
        if (refinementRefs.all.current.max > today) {
            // Min can't be less than min found in results
            refinementRefs.upcoming.current.min = Math.max(today, refinementRefs.all.current.min) + 1;
        }
        if (refinementRefs.all.current.min < today) {
            // Max can't be greater than max found in results
            refinementRefs.past.current.max = Math.min(today, refinementRefs.all.current.max) - 1;
        }
    }

    const rangesLoaded = () => currentRefinement.min || currentRefinement.max;
    const currentIsAll = () =>
        currentRefinement.min !== refinementRefs.upcoming.current.min &&
        currentRefinement.max !== refinementRefs.past.current.max;
    useEffect(() => {
        // If we see a min/max we haven't specified, we know we should switch back to
        // "all" (this is effectively a listener for a "Clear Refinements" action)
        if (appContext.initialRenderComplete && rangesLoaded()) {
            if (currentIsAll()) {
                radioRefs.all.current.querySelector('input').checked = true;
                setDateRangeInQueryString(null);
            }
        }
    }, [currentRefinement]);

    useEffect(() => {
        // As a pseudo-filter, initial date range from URL needs
        // to be applied after the rest of the app has loaded
        if (appContext.initialRenderComplete) {
            const initialDateRange = getDateRangeFromQueryString();
            if (initialDateRange === 'past' || initialDateRange === 'upcoming') {
                refine(refinementRefs[initialDateRange].current);
                radioRefs[initialDateRange].current.querySelector('input').checked = true;
            } else {
                radioRefs.all.current.querySelector('input').checked = true;
            }
        }
    }, [appContext.initialRenderComplete]);

    return (
        <>
            <ul className={style.options}>
                <li ref={radioRefs.all}>
                    <Radio
                        label="All"
                        name="date"
                        onChange={() => {
                            refine(false);
                            setDateRangeInQueryString(null);
                        }}
                    />
                </li>
                <li ref={radioRefs.past}>
                    <Radio
                        label="Past"
                        name="date"
                        onChange={() => {
                            refine(refinementRefs.past.current);
                            setDateRangeInQueryString('past');
                        }}
                        disabled={!refinementRefs.past.current.min && !refinementRefs.past.current.max}
                    />
                </li>
                <li ref={radioRefs.upcoming}>
                    <Radio
                        label="Upcoming"
                        name="date"
                        onChange={() => {
                            refine(refinementRefs.upcoming.current);
                            setDateRangeInQueryString('upcoming');
                        }}
                        disabled={!refinementRefs.upcoming.current.min && !refinementRefs.upcoming.current.max}
                    />
                </li>
            </ul>
        </>
    );
};

const CustomDateListFilter = connectRange(DateList);

type Props = {
    attribute: string;
    label: string;
    description?: string;
};

const WrappedFilter = ({ attribute, label, description }: Props) => {
    return (
        <div className={style.filterWrapper}>
            <div className={style.accordionHeader}>{label}</div>
            <div className={style.description}>{description}</div>
            <CustomDateListFilter attribute={attribute} />
        </div>
    );
};

export default WrappedFilter;
